/**
 * **长按确认。**
 *
 * 评审：
 *
 * > 在濒死期间，我希望按钮变红，且需要长按确认（长按期间有一个按钮上要有一个
 * > 从左到右的条）。
 *
 * 这里只有**计时那一半**（纯粹的 0..1 进度 + "刚好满"那一下），画法在
 * `state.ts` 的 `drawHoldBar`，接线在 `state.ts` 的 `createSurface`。
 * 拆开是为了能在 node 里钉住行为 —— 长按这种东西的 bug（松手还在涨、
 * 满了报两次、离开按钮没取消）全都只在真手里才显形。
 *
 * ⚠️ 时间走的是**每帧的 `deltaMS`**（那唯一一个 ticker），不是 `Date.now()`：
 * 和项目里其它一切动效一样，它要能暂停、能重放（见 `architecture.md` §7）。
 */

/** 长按确认要按多久（毫秒）。评审没给数，取「明显要按住、但不烦人」的一档 */
export const HOLD_CONFIRM_MS = 3000;
/**
 * **触控上「按住看说明」要按多久。**
 *
 * 比确认短得多 —— 它不是确认，只是把 hover 换成「按住」这一路。手牌看 lore、
 * 动作按钮看 tip 都用它，所以只有这一个数。
 */
export const HOLD_HINT_MS = 350;

export interface HoldClock {
  /** 按下：进度归零，开始数 */
  begin(): void;
  /** 松开 / 指针移出：取消（进度立刻归零） */
  cancel(): void;
  /**
   * 推进一帧。返回 `true` **只在刚好满的那一帧**（调用方据此触发确认）——
   * 满了之后再调只会返回 `false`，不会重复触发。
   */
  advance(deltaMS: number): boolean;
  /** 0..1。没在按的时候是 0 */
  readonly progress: number;
  /** 正在按（进度会涨）吗 */
  readonly active: boolean;
}

/**
 * 造一个长按计时器。
 *
 * @param holdMs 要按多久。`<= 0` 的时候 `begin()` 之后第一次 `advance` 就满
 *   （等价于"不用长按"，但调用方一般直接不装这个计时器）
 */
export function createHoldClock(holdMs: number): HoldClock {
  const need = Math.max(0, holdMs);
  let held = 0;
  let running = false;

  return {
    begin(): void {
      held = 0;
      running = true;
    },
    cancel(): void {
      held = 0;
      running = false;
    },
    advance(deltaMS: number): boolean {
      if (!running) return false;
      // ⚠️ 负数（时钟倒着走）不该把进度往回带 —— 只认正的推进
      held += Math.max(0, deltaMS);
      if (held < need) return false;
      held = need;
      // ⚠️ 满了就**停下**：`running = false` 保证这一帧之后不再涨
      //    （手指还按着，但事情已经发生了），于是 `advance` 只会报一次
      running = false;
      return true;
    },
    get progress(): number {
      // ⚠️ 满了之后**留在满格**，直到松手（`cancel`）—— 条子涨满再"啪"地弹回
      //    去读起来像"没按成"，而它是"按成了、正在等结果"
      if (!running && held > 0) return 1;
      if (need <= 0) return running ? 1 : 0;
      return Math.min(1, held / need);
    },
    get active(): boolean {
      return running;
    },
  };
}
